import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { useFetchData } from './useFetchData.ts';
import type {
	QueryResult,
	UseExperimentalQueryParams,
	QueryCallbackOptions,
	QueryExecutionOptions,
	FetchPolicy,
} from './useExperimentalQuery.ts';
import { useExperimentalQueryExecution } from './useExperimentalQueryExecution.ts';
import { INITIAL_IDLE_STATE } from './useExperimentalQueryState.ts';
import { generateCacheKey } from './generateCacheKey.ts';

type LazyQueryFunctionOptions<TData, TVariables> = QueryCallbackOptions<TData> &
	QueryExecutionOptions & {
		variables?: TVariables;
	};

export type LazyQueryResult<TData, TVariables> = [
	(options?: LazyQueryFunctionOptions<TData, TVariables>) => void,
	QueryResult<TData> & { stopPolling: () => void },
];

export function useExperimentalLazyQuery<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	credentials,
	parseResult,
	onCompleted,
	onError,
	transformError,
	fetchPolicy = 'cache-first',
	variables,
	pollInterval,
}: UseExperimentalQueryParams<TData, TVariables, TResponse>): LazyQueryResult<TData, TVariables> {
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
		parseResult,
	});

	const key = generateCacheKey({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
	});

	return useExperimentalLazyQueryBase<TData, TVariables>({
		key,
		fetchData,
		onCompleted,
		onError,
		transformError,
		fetchPolicy,
		pollInterval,
	});
}

type UseExperimentalLazyQueryBaseParams<TData, TVariables> = QueryCallbackOptions<TData> & {
	key: string;
	fetchData: (variables?: TVariables) => TData | Promise<TData>;
	fetchPolicy: FetchPolicy;
	pollInterval?: number;
};

export function useExperimentalLazyQueryBase<TData, TVariables>(
	params: UseExperimentalLazyQueryBaseParams<TData, TVariables>,
): LazyQueryResult<TData, TVariables> {
	const {
		queryState,
		execute: baseExecute,
		stopPolling,
	} = useExperimentalQueryExecution<TData, TVariables>({
		...params,
		isLazyQuery: true,
	});

	const execute = useStableCallback(
		async (options?: LazyQueryFunctionOptions<TData, TVariables>) => {
			await baseExecute(options?.variables);
		},
	);

	return [execute, { ...(queryState ?? INITIAL_IDLE_STATE), stopPolling }];
}
